// eslint-disable-next-line no-undef
class TableOfContentsObserver extends HTMLElement {
  constructor () {
    super()
    this.toc = null
    this.footer = null
    this.header = null
    this.observer = null
    this.headerHeight = 0
    this.baseHeaderHeight = 0
    this.bestHeadersHeight = 0
    this.footerHeight = 0
    this.tocNaturalHeight = 0
    this.bestHeadersObserver = null
    this.init()
  }

  init () {
    document.addEventListener('DOMContentLoaded', () => {
      this.toc = document.querySelector('.toc')
      this.footer = document.querySelector('.sleepdoctor-footer, .footer, footer')
      this.header = document.querySelector('sticky-header, .header, header')

      if (!this.toc || !this.footer) return

      this.calculateHeights()
      this.setupPositioning()
      this.setupObserver()
      this.setupBestHeadersObserver()
      this.setupScrollListener()

      window.addEventListener('resize', () => {
        this.calculateHeights()
        this.setupPositioning()
      })
    })
  }

  calculateHeights () {
    this.baseHeaderHeight = 0

    if (this.header) {
      this.baseHeaderHeight += this.header.offsetHeight
    }

    const announcementBar = document.querySelector('.sleepdoctor-announcement-bar')
    if (announcementBar) {
      this.baseHeaderHeight += announcementBar.offsetHeight
    }

    this.baseHeaderHeight += 20

    this.bestHeadersHeight = 0
    const sdBestContainer = document.querySelector('.sd-best')
    const sdBestIndividualContainer = document.querySelector('.sd-best-individual')

    if (sdBestContainer) {
      const bestHeader = sdBestContainer.querySelector('.sd-best-header')
      if (bestHeader) {
        this.bestHeadersHeight += bestHeader.offsetHeight
      }

      const bestAffText = sdBestContainer.querySelector('.sd-best-aff-text')
      if (bestAffText) {
        this.bestHeadersHeight += bestAffText.offsetHeight
      }
    } else if (sdBestIndividualContainer) {
      const bestAffText = sdBestIndividualContainer.querySelector('.sd-best-aff-text')
      if (bestAffText) {
        this.bestHeadersHeight += bestAffText.offsetHeight
      }
    }

    this.headerHeight = this.baseHeaderHeight + this.bestHeadersHeight

    if (this.footer) {
      this.footerHeight = this.footer.offsetHeight
    }

    if (this.toc && this.tocNaturalHeight === 0) {
      const originalMaxHeight = this.toc.style.maxHeight
      const originalHeight = this.toc.style.height
      this.toc.style.maxHeight = ''
      this.toc.style.height = ''
      this.tocNaturalHeight = this.toc.scrollHeight
      this.toc.style.maxHeight = originalMaxHeight
      this.toc.style.height = originalHeight
    }
  }

  setupPositioning () {
    this.toc.style.top = `${this.headerHeight}px`

    const containerWidth = 1200
    const tocWidth = 280
    const padding = 40
    const leftShift = 120

    if (window.innerWidth >= containerWidth + tocWidth + (padding * 2)) {
      const rightPosition = (window.innerWidth - containerWidth) / 2 - tocWidth - padding + leftShift
      this.toc.style.right = `${Math.max(rightPosition, 20)}px`
    } else {
      this.toc.style.right = '20px'
    }
  }

  setupObserver () {
    const thresholds = []
    for (let i = 0; i <= 1.0; i += 0.01) {
      thresholds.push(i)
    }

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: thresholds
    }

    // eslint-disable-next-line no-undef
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const footerRect = entry.boundingClientRect
          const visibleFooterHeight = Math.max(0, window.innerHeight - footerRect.top)

          this.toc.classList.add('toc--bottom')
          this.toc.style.position = 'fixed'
          this.toc.style.top = `${this.headerHeight}px`

          const footerFullyVisible = footerRect.top <= window.innerHeight && footerRect.bottom >= 0 &&
                                     footerRect.top >= 0 && visibleFooterHeight >= this.footerHeight
          const additionalOffset = footerFullyVisible ? 20 : 0
          const availableHeight = window.innerHeight - this.headerHeight - visibleFooterHeight - 40 - additionalOffset

          if (this.tocNaturalHeight > availableHeight) {
            this.toc.style.maxHeight = `${Math.max(200, availableHeight)}px`
            this.toc.style.overflowY = 'auto'
          } else {
            this.toc.style.maxHeight = `${this.tocNaturalHeight}px`
            this.toc.style.overflowY = ''
          }
        } else {
          this.toc.classList.remove('toc--bottom')
          this.toc.style.position = 'fixed'
          this.toc.style.top = `${this.headerHeight}px`
          this.toc.style.maxHeight = ''
          this.toc.style.overflowY = ''
        }
      })
    }, options)

    this.observer.observe(this.footer)
  }

  setupBestHeadersObserver () {
    if (this.bestHeadersHeight === 0) return

    const sdBestContainer = document.querySelector('.sd-best')
    const sdBestIndividualContainer = document.querySelector('.sd-best-individual')

    let lastBestElement = null

    if (sdBestContainer) {
      lastBestElement = sdBestContainer.querySelector('.sd-best-aff-text') || sdBestContainer.querySelector('.sd-best-header')
    } else if (sdBestIndividualContainer) {
      lastBestElement = sdBestIndividualContainer.querySelector('.sd-best-aff-text')
    }

    if (!lastBestElement) return

    const options = {
      root: null,
      rootMargin: `-${this.baseHeaderHeight}px 0px 0px 0px`,
      threshold: 0
    }

    // eslint-disable-next-line no-undef
    this.bestHeadersObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!this.toc.classList.contains('toc--bottom')) {
          if (!entry.isIntersecting) {
            this.toc.style.top = `${this.baseHeaderHeight}px`
          } else {
            this.toc.style.top = `${this.headerHeight}px`
          }
        }
      })
    }, options)

    this.bestHeadersObserver.observe(lastBestElement)
  }

  setupScrollListener () {
    let ticking = false

    const updateTocHeight = () => {
      const footerRect = this.footer.getBoundingClientRect()

      if (footerRect.top < window.innerHeight) {
        const visibleFooterHeight = Math.max(0, window.innerHeight - footerRect.top)

        const footerFullyVisible = footerRect.top <= window.innerHeight && footerRect.bottom >= 0 &&
                                   footerRect.top >= 0 && visibleFooterHeight >= this.footerHeight
        const additionalOffset = footerFullyVisible ? 20 : 0
        const availableHeight = window.innerHeight - this.headerHeight - visibleFooterHeight - 40 - additionalOffset

        if (this.tocNaturalHeight > availableHeight) {
          this.toc.style.maxHeight = `${Math.max(200, availableHeight)}px`
          this.toc.style.overflowY = 'auto'
        } else {
          this.toc.style.maxHeight = `${this.tocNaturalHeight}px`
          this.toc.style.overflowY = ''
        }
      } else {
        this.toc.style.maxHeight = ''
        this.toc.style.overflowY = ''
      }

      ticking = false
    }

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(updateTocHeight)
        ticking = true
      }
    })
  }

  disconnectedCallback () {
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.bestHeadersObserver) {
      this.bestHeadersObserver.disconnect()
    }
  }
}

// eslint-disable-next-line no-undef
customElements.define('table-of-contents-observer', TableOfContentsObserver)
